import React, { useState, useEffect, KeyboardEvent, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import {
    Button,
    GradientDot,
    IconTrqkMark,
    ValidatedInput,
    LoadingEllipses,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { getWebpackEnvVar, locale, encodeURLParams } from '@trqk/utils';
import { validateInputs } from '@trqk/hooks';

import { AuthLayout } from '../../templates';
import { PATHS, URL_PARAMS } from '../../../constants';
import { inputIds } from './constants';

const { FORGOT_PASSWORD } = queryStrings;

const centerColumnWidth = 350;

/**
 * A form that allows a user to reset their password. This is step 1 of a two-step process; the
 * second step is in ConfirmPasswordReset.
 */
export const ForgotPassword = (): JSX.Element => {
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const [email, setEmail] = useState('');
    const [didSubmit, setDidSubmit] = useState(false);
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);
    const [forgotPasswordErrorMessage, setForgotPasswordErrorMessage] =
        useState('');
    const history = useHistory();

    const [forgotPassword, { loading }] = useMutation(FORGOT_PASSWORD, {
        onCompleted: () => {
            history.push(
                `${PATHS.CONFIRM_PASSWORD_RESET}?${encodeURLParams({
                    [URL_PARAMS.EMAIL]: email,
                })}`,
            );
        },
        onError: (): void => {
            setForgotPasswordErrorMessage(
                locale.enUS.FORGOT_PASSWORD.ERRORS.REQUEST_CODE_ERROR,
            );
        },
    });

    const validate = useCallback(
        () =>
            validateInputs([
                {
                    inputId: inputIds.forgotPasswordEmail,
                    label: 'Email',
                    type: 'email',
                    value: email,
                    required: true,
                },
            ]),
        [email],
    );

    useEffect(() => {
        if (didSubmit) {
            const validationResults = validate();

            setValidationOutputs(validationResults.validationOutputs);
        }
    }, [didSubmit, validate]);

    const handleSubmit = (): void => {
        setDidSubmit(true);

        const validationResults = validate();

        if (!validationResults.hasValidationErrors) {
            forgotPassword({
                variables: {
                    clientId,
                    username: email,
                    userPoolId,
                },
            });
        } else {
            setValidationOutputs(validationResults.validationOutputs);
        }
    };

    return (
        <AuthLayout>
            <div className="flex flex-col h-full">
                <div className="flex-grow flex flex-col justify-center items-center">
                    <div style={{ width: centerColumnWidth }}>
                        <div className="flex justify-center">
                            <GradientDot gradientColor="white" width={120}>
                                <IconTrqkMark width={50} />
                            </GradientDot>
                        </div>
                        <div>
                            <h3 className="text-white font-medium text-center my-4">
                                {locale.enUS.FORGOT_PASSWORD.TITLE}
                            </h3>
                            <div className="text-center text-white my-6">
                                {locale.enUS.FORGOT_PASSWORD.CTA}
                            </div>
                            <div className="text-center text-white text-sm h-6">
                                {forgotPasswordErrorMessage && (
                                    <>{forgotPasswordErrorMessage}</>
                                )}
                            </div>
                            <ValidatedInput
                                addClasses="my-2"
                                inputId={inputIds.forgotPasswordEmail}
                                icon="email"
                                type="email"
                                placeholder="Email"
                                value={email}
                                handleChange={setEmail}
                                handleKeyPress={(
                                    e: KeyboardEvent<Element>,
                                ): void => {
                                    if (e.key === 'Enter') {
                                        handleSubmit();
                                    }
                                }}
                                padding="large"
                                validationOutputs={validationOutputs}
                            />
                        </div>
                        <div className="mt-10">
                            <Button
                                color="primary-dark-bg"
                                handleClick={handleSubmit}
                            >
                                <div className="text-center tracking-wider">
                                    {!loading && (
                                        <div>
                                            {
                                                locale.enUS.FORGOT_PASSWORD
                                                    .BUTTONS.REQUEST_CODE
                                            }
                                        </div>
                                    )}
                                    {loading && <LoadingEllipses width={40} />}
                                </div>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};
